import { AlertTriangle } from "lucide-react";

import type { ClinicLearner, MedicalRecord } from "../../services/medicalService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field } from "../FormLayout";
import { SearchPick } from "../library/Pickers";
import { clinicalAlerts, learnerLine } from "./medicalLogic";

export const errorLine = (error: unknown, fallback: string) => (
  <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(error, fallback)}</p>
);

/**
 * Allergies and long-term conditions, shown wherever a learner is treated or
 * prescribed for, so nobody has to go and look them up.
 */
export function ClinicalAlerts({ record, missingText }: { record?: MedicalRecord | null; missingText?: string }) {
  const alerts = clinicalAlerts(record);
  if (!record) {
    return missingText ? <p className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600">{missingText}</p> : null;
  }
  if (!alerts.length) return <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">No allergies or long-term conditions recorded.</p>;
  return (
    <div className="space-y-2">
      {alerts.map((alert) => (
        <div
          key={alert.label}
          className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${alert.tone === "danger" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">{alert.label}:</span> <span className="whitespace-pre-wrap">{alert.text}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Type a name, class or learner number, then pick. */
export function LearnerPick({ learners, value, onChange, label = "Learner", empty = "No learner matches" }: {
  learners: ClinicLearner[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  empty?: string;
}) {
  return (
    <Field label={label} as="div" className="sm:col-span-2">
      <SearchPick
        items={learners}
        value={value}
        onChange={onChange}
        label={(learner) => learner.full_name}
        detail={learnerLine}
        text={(learner) => `${learner.full_name} ${learner.student_id}`}
        placeholder="Search by name, class or learner number"
        empty={empty}
        required
      />
    </Field>
  );
}

/** A short piece of text for a details list, or nothing. */
export const orNothing = (...parts: (string | number | null | undefined | false)[]) => parts.filter((part) => part !== null && part !== undefined && part !== false && part !== "").join(" ") || undefined;
